import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtNum, fmtDate, aktuellerMonat, downloadCsv } from "@/lib/api";
import { MonthPicker } from "@/components/app/MonthPicker";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Milk, Download, Loader2, Search } from "lucide-react";

export default function LieferungenPage() {
  const { user, liefernummer } = useAuth();
  const [monat, setMonat] = useState(aktuellerMonat());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suche, setSuche] = useState("");

  const istLieferant = user?.rolle === "lieferant";

  useEffect(() => {
    setLoading(true);
    const params = { monat };
    if (istLieferant && liefernummer) params.liefernummer = liefernummer;
    api
      .get("/lieferungen", { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [monat, liefernummer, istLieferant]);

  const gefiltert = rows.filter(
    (r) => !suche || r.liefernummer.includes(suche) || r.datum.includes(suche) || (r.tour || "").toLowerCase().includes(suche.toLowerCase())
  );
  const summe = gefiltert.reduce((acc, r) => acc + (r.menge_kg || 0), 0);

  const exportCsv = () =>
    downloadCsv(
      `Lieferungen_${monat}.csv`,
      ["Datum", "Uhrzeit", "Liefernummer", "Menge kg", "Temperatur °C", "Probe", "Probenummer", "Tour"],
      gefiltert.map((r) => [r.datum, r.uhrzeit, r.liefernummer, r.menge_kg, r.temperatur ?? "", r.probe_gezogen ? "ja" : "nein", r.probenummer ?? "", r.tour])
    );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Lieferungen</h1>
          <p className="text-sm text-muted-foreground">
            {istLieferant ? `Liefernummer ${liefernummer}` : "Alle Lieferanten"} · {fmtNum(gefiltert.length)} Lieferungen · {fmtNum(summe)} kg
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="deliveries-search-input"
              className="pl-8 w-[180px] bg-card"
              placeholder="Suchen …"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
            />
          </div>
          <MonthPicker value={monat} onChange={setMonat} />
          <Button data-testid="deliveries-csv-export-button" variant="secondary" onClick={exportCsv} disabled={!gefiltert.length}>
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : gefiltert.length ? (
            <div className="overflow-x-auto">
              <Table data-testid="deliveries-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-card">Datum</TableHead>
                    <TableHead>Uhrzeit</TableHead>
                    {!istLieferant && <TableHead>Liefernr.</TableHead>}
                    <TableHead className="text-right">Menge (kg)</TableHead>
                    <TableHead className="text-right">Temp. (°C)</TableHead>
                    <TableHead>Probe</TableHead>
                    <TableHead>Tour</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gefiltert.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="sticky left-0 bg-card font-medium">{fmtDate(r.datum)}</TableCell>
                      <TableCell className="font-num">{r.uhrzeit}</TableCell>
                      {!istLieferant && <TableCell className="font-num">{r.liefernummer}</TableCell>}
                      <TableCell className="text-right font-num">{fmtNum(r.menge_kg)}</TableCell>
                      <TableCell className="text-right font-num">{fmtNum(r.temperatur, 1)}</TableCell>
                      <TableCell>
                        {r.probe_gezogen ? (
                          <Badge variant="secondary" title={r.probenummer}>Probe</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">–</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{r.tour}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState icon={Milk} title="Keine Lieferungen für den gewählten Monat." text="Wählen Sie einen anderen Monat oder prüfen Sie den ÖNORM-Import." />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
